import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IMask from 'imask';
import { useSaveLaboratory } from './hooks/useSaveLaboratory';

// Laboratories often have landlines, so both formats are accepted. The mask is picked by the
// digit count: a single dynamic mask locks on the landline format and drops the 11th digit.
const formatLandline = IMask.createPipe({ mask: '(00) 0000-0000' });
const formatMobile = IMask.createPipe({ mask: '(00) 00000-0000' });

const formatPhone = (value) => {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length > 10 ? formatMobile(digits) : formatLandline(digits);
};

export default function LaboratoryFormModal({ open, onClose, onSave, laboratory }) {
  const navigate = useNavigate();
  const { save, isSaving } = useSaveLaboratory();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(laboratory?.name ?? '');
      setPhone(formatPhone(laboratory?.phone ?? ''));
      setContactName(laboratory?.contactName ?? '');
      setError('');
    }
  }, [open, laboratory]);

  const isValid = name.trim() !== '';

  const submit = () => {
    if (!isValid) return;

    const payload = {
      name: name.trim(),
      phone: phone.trim() || null,
      contactName: contactName.trim() || null,
    };

    save(laboratory ? laboratory.id : null, payload)
      .then((returnFromApi) => {
        onSave(returnFromApi.data, payload);
        onClose();
      })
      .catch((returnFromApi) => {
        const data = returnFromApi.response?.data;
        setError(typeof data === 'string' && data ? data : 'Erro ao salvar laboratório.');
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {laboratory ? 'Editar Laboratório' : 'Novo Laboratório'}
      </DialogTitle>
      <DialogContent>
        <Collapse in={Boolean(error)} sx={{ mb: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Collapse>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Nome *"
            placeholder="Ex: Laboratório Dental Arte"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            fullWidth
            label="Telefone/WhatsApp"
            value={phone}
            onChange={(event) => setPhone(formatPhone(event.target.value))}
            placeholder="(00) 00000-0000"
            inputProps={{ inputMode: 'tel' }}
          />
          <TextField
            fullWidth
            label="Pessoa de contato"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!isValid || isSaving}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

LaboratoryFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  laboratory: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    phone: PropTypes.string,
    contactName: PropTypes.string,
  }),
};

LaboratoryFormModal.defaultProps = {
  laboratory: null,
};
